// Клиент публичных эндпоинтов Legal Center (raw Fastify, не tRPC).
//
//   GET /api/legal/documents        — манифест;
//   GET /api/legal/documents/:key   — тело редакции в Markdown.
//
// Почему не tRPC: `legal.document` — `protectedProcedure`, а читалка обязана
// открываться БЕЗ сессии. Ссылку на документ мы кладём в баннер cookie, в
// футер и в письма; упершись в форму входа, она перестала бы быть
// информированием и стала бы препятствием (GDPR ст. 7(2), ст. 12(1)).
//
// Сессия сюда не передаётся намеренно (`credentials` не выставлен): эндпоинт
// ничего пользовательского не отдаёт, и лишний cookie в запросе только сделал
// бы публичный кэш на прокси приватным.
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { z } from "zod";

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/** Ответ границы валидируется: снаружи приходит JSON, а не типы. */
const LocalizedVersionSchema = z.object({
	locale: z.string(),
	title: z.string(),
	sha256: z.string(),
	url: z.string(),
});

/**
 * Форма публичного манифеста (`getPublicManifest`). Редакции сейчас только
 * на `en` (Д10), поэтому `locales` на проводе нет — читалка не должна из-за
 * этого падать. Поле оставляем опциональным: если манифест начнёт отдавать
 * список локалей, переключатель в читалке заработает сам.
 */
const ManifestItemSchema = z.object({
	key: z.string(),
	version: z.string(),
	sha256: z.string(),
	locale: z.string(),
	title: z.string(),
	requiresAcceptance: z.boolean(),
	materialChange: z.boolean(),
	effectiveFrom: z.string(),
	publishedAt: z.string().nullable(),
	icon: z.string(),
	order: z.number(),
	url: z.string(),
	locales: z.array(LocalizedVersionSchema).optional().default([]),
});

export const ManifestSchema = z.object({
	issuer: z.string(),
	generatedAt: z.string().nullable(),
	documents: z.array(ManifestItemSchema),
});

export const DocumentSchema = ManifestItemSchema.extend({
	bodyMarkdown: z.string(),
	/** Локаль, на которой редакция реально нашлась, — может отличаться от запрошенной. */
	resolvedLocale: z.string(),
});

export type PublicLegalManifest = z.infer<typeof ManifestSchema>;
export type PublicLegalDocument = z.infer<typeof DocumentSchema>;

/** Документа с таким ключом нет — отличается от сетевой ошибки. */
export class LegalDocumentNotFound extends Error {
	constructor(key: string) {
		super(`Документ ${key} не опубликован`);
		this.name = "LegalDocumentNotFound";
	}
}

async function fetchPublicManifest(locale: string): Promise<PublicLegalManifest> {
	const response = await fetch(
		`${API_BASE}/api/legal/documents?locale=${encodeURIComponent(locale)}`,
	);
	if (!response.ok) throw new Error(`legal manifest: HTTP ${response.status}`);
	return ManifestSchema.parse(await response.json());
}

async function fetchPublicDocument(key: string, locale: string): Promise<PublicLegalDocument> {
	const response = await fetch(
		`${API_BASE}/api/legal/documents/${encodeURIComponent(key)}?locale=${encodeURIComponent(locale)}`,
	);
	if (response.status === 404) throw new LegalDocumentNotFound(key);
	if (!response.ok) throw new Error(`legal document: HTTP ${response.status}`);
	return DocumentSchema.parse(await response.json());
}

/** Список документов правового центра — публичный, без сессии. */
export function usePublicLegalManifest(locale: string): UseQueryResult<PublicLegalManifest> {
	return useQuery({
		queryKey: ["legal", "manifest", locale],
		queryFn: () => fetchPublicManifest(locale),
		// Редакции меняются раз в месяцы, а эндпоинт отдаёт `max-age=300`.
		staleTime: 5 * 60_000,
	});
}

/**
 * `enabled: false` нужен модалке согласия: документы шага «Документы» тянутся
 * только тогда, когда человек действительно открыл текст, а не при показе
 * экрана с тремя галочками.
 */
export function usePublicLegalDocument(
	key: string,
	locale: string,
	enabled = true,
): UseQueryResult<PublicLegalDocument> {
	return useQuery({
		queryKey: ["legal", "document", key, locale],
		queryFn: () => fetchPublicDocument(key, locale),
		enabled,
		staleTime: 5 * 60_000,
		// Несуществующий ключ повторять бессмысленно.
		retry: (failureCount, error) =>
			error instanceof LegalDocumentNotFound ? false : failureCount < 2,
	});
}
